type WorkerRequest = {
  id: number;
  sourceSeed: number;
  version?: BlinkoVersion;
};

type WorkerSuccessResponse = {
  id: number;
  success: true;
  letterSeed: number;
  ballSeeds: number[];
  bonusSeedCounter: number;
};

type WorkerErrorResponse = {
  id: number;
  success: false;
  error: string;
};

type WorkerResponse = WorkerSuccessResponse | WorkerErrorResponse;

import { BlinkoVersion } from './game-engine';
import type { SeedPlan } from './seedPlan';

type SeedPlanResult = SeedPlan;

class SeedPlanWorkerClient {
  private worker: Worker | null = null;
  private requestId = 0;
  private pending = new Map<number, { resolve: (value: SeedPlanResult) => void; reject: (reason?: any) => void }>();

  private ensureWorker() {
    if (this.worker) return;
    this.worker = new Worker(new URL('./workers/seedPlanner.worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const data = event.data;
      const pending = this.pending.get(data.id);
      if (!pending) return;
      this.pending.delete(data.id);
      if (data.success) {
        pending.resolve({
          letterSeed: data.letterSeed,
          ballSeeds: data.ballSeeds,
          bonusSeedCounter: data.bonusSeedCounter,
        });
      } else {
        pending.reject(new Error(data.error));
      }
    };
    this.worker.onerror = (error) => {
      this.pending.forEach(({ reject }) => reject(error));
      this.pending.clear();
    };
  }

  public async requestPlan(version: BlinkoVersion, sourceSeed: number): Promise<SeedPlanResult> {
    this.ensureWorker();
    return new Promise<SeedPlanResult>((resolve, reject) => {
      const id = ++this.requestId;
      this.pending.set(id, { resolve, reject });
      this.worker!.postMessage({ id, version, sourceSeed } satisfies WorkerRequest);
    });
  }
}

export const seedPlanWorkerClient = new SeedPlanWorkerClient();
export type { SeedPlanResult };
